import React from "react"
import { Button } from "components/button"
import { useMutation, useQueryClient } from "react-query"
import Axios from "axios"
import { useSnackbarContext } from "components/snackbar"
import { MdCloudDownload } from "react-icons/md"
import { useTranslation } from "react-i18next"

interface ExportCardProps {
  exportType: string
  title: string
  description: string
  icon?: React.ReactNode
}

const ExportCard: React.FC<ExportCardProps> = ({
  exportType,
  title,
  description,
  icon,
}) => {
  const { t } = useTranslation()
  const tBase = "views.admin.export.components.ExportCard"
  const tr = (key: string) => t(`${tBase}.${key}`)

  const { showSnackbar } = useSnackbarContext()
  const queryClient = useQueryClient()
  const [archiveFilter, setArchiveFilter] = React.useState<"active" | "archived" | "both">("active")
  const [includeProperties, setIncludeProperties] = React.useState<boolean>(false)

  const exportMutation = useMutation(
    async () => {
      const options: any = {}
      if (exportType === "inventory" || exportType === "services" || exportType === "groups") {
        options.archiveFilter = archiveFilter
      }
      if (exportType === "customers") {
        options.includeProperties = includeProperties
      }
      const response = await Axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/export-tool/start`,
        { export_type: exportType, options }
      )
      return response.data
    },
    {
      onSuccess: () => {
        showSnackbar(tr("Export started! You will receive an email when ready."))
        // Immediately refetch export jobs to show the new export
        queryClient.invalidateQueries("export-jobs")
      },
      onError: (error) => {
        console.error(error)
        showSnackbar(tr("Failed to start export"))
      }
    }
  );

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200 hover:border-blue-300 transition-colors flex flex-col h-full">
      <div className="flex items-start justify-between flex-1">
        <div className="flex items-center gap-3">
          {icon && <div className="text-2xl text-gray-600">{icon}</div>}
          <div>
            <h3 className="font-semibold text-gray-800">{title}</h3>
            <p className="text-sm text-gray-500 mt-1">{description}</p>
          </div>
        </div>
      </div>
      <div className="mt-4 flex justify-between items-end">
        <Button
          primary
          size="small"
          isLoading={exportMutation.isLoading}
          onClick={() => exportMutation.mutate()}
        >
          <MdCloudDownload style={{ marginRight: "6px", fontSize: "16px" }} />
          {tr("Export")}
        </Button>
        {(exportType === "inventory" || exportType === "services" || exportType === "groups") && (
          <div className="">
            <label htmlFor={`archive-filter-${exportType}`} className="block text-xs font-medium text-gray-700 mb-1">
              {tr("Include Only")}
            </label>
            <select
              id={`archive-filter-${exportType}`}
              value={archiveFilter}
              onChange={(e) => setArchiveFilter(e.target.value as "active" | "archived" | "both")}
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="active">{tr("Active")}</option>
              <option value="archived">{tr("Archived")}</option>
              <option value="both">{tr("Both")}</option>
            </select>
          </div>
        )}
        {exportType === "customers" && (
          <div className="">
            <label htmlFor={`include-properties-${exportType}`} className="block text-xs font-medium text-gray-700 mb-1">
              {tr("Include Properties")}
            </label>
            <select
              id={`include-properties-${exportType}`}
              value={includeProperties.toString()}
              onChange={(e) => setIncludeProperties(e.target.value === "true")}
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="false">{tr("False")}</option>
              <option value="true">{tr("True")}</option>
            </select>
          </div>
        )}
      </div>
    </div>
  )
}

export default ExportCard
